import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Button,
  MenuItem,
  SwipeableDrawer,
  List,
  Divider,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Slide,
  useScrollTrigger,
  alpha,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Home,
  Info,
  Web,
  Brush,
  Map,
  ChevronRight,
} from "@mui/icons-material";

// Assets
import logo from "../../assets/images/logo.png";

const MENU_ITEMS = [
  { path: "/", label: "Home", icon: Home },
  { path: "/webdesign", label: "Web Design", icon: Web },
  { path: "/marketing", label: "Digital Marketing", icon: Brush },
  { path: "/gis", label: "GIS Services", icon: Map },
  { path: "/art", label: "Art Sales", icon: Brush },
  { path: "/about", label: "About", icon: Info },
];

export default function Header({ active, landing }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100,
  });

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const renderLogo = (props = {}) => (
    <Box
      component="img"
      src={logo}
      alt="Jeremy Solutions Logo"
      onClick={() => handleNavigation("/")}
      sx={{
        height: { xs: "44px", md: "54px" },
        objectFit: "contain",
        cursor: "pointer",
        transition: "transform 0.3s ease",
        "&:hover": {
          transform: "scale(1.05)",
        },
        ...props.sx,
      }}
    />
  );

  const renderDrawer = () => (
    <SwipeableDrawer
      anchor="right"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      onOpen={() => setDrawerOpen(true)}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 320 },
          p: 2,
          background: (theme) =>
            `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        },
      }}
    >
      <Stack spacing={3}>
        {renderLogo({ sx: { alignSelf: "center", height: "84px" } })}

        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            textAlign: "center",
            color: "common.white",
          }}
        >
          Jeremy Solutions
        </Typography>

        <Divider sx={{ borderColor: "primary.light" }} />

        <List sx={{ width: "100%" }}>
          {MENU_ITEMS.map(({ path, label, icon: Icon }) => (
            <ListItemButton
              key={path}
              selected={isActive(path)}
              onClick={() => handleNavigation(path)}
              sx={{
                borderRadius: 1,
                mb: 1,
                color: "common.white",
                "&.Mui-selected": {
                  bgcolor: alpha("#fff", 0.1),
                  "&:hover": {
                    bgcolor: alpha("#fff", 0.15),
                  },
                },
                "&:hover": {
                  bgcolor: alpha("#fff", 0.08),
                },
              }}
            >
              <ListItemIcon sx={{ color: "inherit" }}>
                <Icon />
              </ListItemIcon>
              <ListItemText primary={label} />
              <ChevronRight sx={{ opacity: 0.5 }} />
            </ListItemButton>
          ))}
        </List>

        <Button
          variant="contained"
          color="secondary"
          size="large"
          onClick={() => handleNavigation("/contact")}
          sx={{
            color: "common.white",
            py: 1.5,
            borderRadius: 2,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: 4,
            },
          }}
        >
          Contact Us
        </Button>
      </Stack>
    </SwipeableDrawer>
  );

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      <AppBar
        position="fixed"
        elevation={trigger ? 4 : 0}
        sx={{
          background: trigger
            ? "rgba(255, 255, 255, 0.9)"
            : "rgba(255, 255, 255, 1)",
          backdropFilter: "blur(8px)",
          color: "primary.main",
          transition: "all 0.3s",
        }}
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ height: { xs: 64, md: 80 } }}>
            {renderLogo()}

            {/* Desktop Menu */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                ml: "auto",
                display: { xs: "none", md: "flex" },
              }}
            >
              {MENU_ITEMS.map(({ path, label }) => (
                <MenuItem
                  key={path}
                  onClick={() => handleNavigation(path)}
                  sx={{
                    color: isActive(path) ? "secondary.main" : "inherit",
                    fontWeight: isActive(path) ? 700 : 500,
                    position: "relative",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      bottom: 0,
                      left: "50%",
                      width: isActive(path) ? "100%" : "0%",
                      height: "2px",
                      bgcolor: "secondary.main",
                      transform: "translateX(-50%)",
                      transition: "width 0.3s ease",
                    },
                    "&:hover::after": {
                      width: "100%",
                    },
                  }}
                >
                  {label}
                </MenuItem>
              ))}

              <Button
                variant="contained"
                color="primary"
                onClick={() => handleNavigation("/contact")}
                sx={{
                  ml: 2,
                  px: 3,
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Contact
              </Button>
            </Stack>

            {/* Mobile Menu Button */}
            <IconButton
              size="large"
              onClick={() => setDrawerOpen(true)}
              sx={{
                ml: "auto",
                display: { xs: "flex", md: "none" },
                color: "inherit",
              }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>

        {renderDrawer()}
      </AppBar>
    </Slide>
  );
}
